import React, { useEffect } from 'react';
import { APITypes, Dynamo } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import Text from '../../v2BaseComponents/Text';
import { FormStack } from '../../v2BaseComponents/Stack';
import { Field } from 'formik';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import { SelectOption } from '../../v2BaseComponents/Inputs/Select/types';
import { codeStatusTypeMessages } from '../../../utils/sharedMessages';
import { required } from '../../../utils/validators';
import FilesField, { FileFieldValue, getAddedFileDefaultValue, mapFormValueToAPIFile } from './FilesField';
import * as Linking from 'expo-linking';
import Link from '../../v2BaseComponents/Link';
import { advancedDirectiveFormsUrl } from '../../../utils/constants';
import { useCodeStatusDescriptiveLabel } from '../../../hooks/useCodeStatusDescriptiveLabel';

const intl = getIntl('CodeStatusFormFields', {
    codeStatusTitle: 'Define your Code Status preferences',
    codeStatusNotesTitle: 'Notes',
    advancedDirectiveForms: 'Advanced Directive Forms',
    advancedDirectiveFormTitle: 'Advanced Directive Form #{index}',
    advancedDirectiveFormRequiredForDNR: 'An Advanced Directive Form is required for DNR',
    findYourStatesForm: 'Find your state’s form',
});

export type FormValue = Omit<APITypes.medicalProfileService.UpsertProfileCodeStatusRequest, 'codeStatusFiles'> & {
    codeStatusFiles: FileFieldValue[];
};

export const getDefaultFromValue = (
    existingRecord: APITypes.medicalProfileService.GetProfileCodeStatusResponse,
): FormValue => {
    if (!existingRecord) {
        return {
            codeStatus: 'full-code',
            codeStatusNotes: '',
            codeStatusFiles: [],
        };
    } else {
        return {
            codeStatus: existingRecord.codeStatus,
            codeStatusNotes: existingRecord.codeStatusNotes || '',
            codeStatusFiles: existingRecord.codeStatusFiles.map((file) => ({
                fileId: file.fileId,
                name: file.name,
            })),
        };
    }
};

export const formValueToUpsertProfileCodeStatusRequest = (
    values: FormValue,
    existingRecord: APITypes.medicalProfileService.GetProfileCodeStatusResponse,
): APITypes.medicalProfileService.UpsertProfileCodeStatusRequest => {
    return {
        ...values,
        codeStatusFiles: mapFormValueToAPIFile(values.codeStatusFiles, existingRecord?.codeStatusFiles || []),
    };
};

export type CodeStatusFormFieldsOwnProps = {
    values: FormValue;
    setFieldValue: (field: string, value: any) => void;
};

export type CodeStatusFormFieldsProps = CodeStatusFormFieldsOwnProps;

const codeStatuses: Dynamo.CodeStatus[] = [
    'full-code',
    'partial-code-dni',
    'partial-code-other',
    'dnr',
    'comfort-measures',
];

const codeStatusSelectOptions: SelectOption[] = codeStatuses.map((codeStatus) => ({
    label: codeStatusTypeMessages(codeStatus),
    value: codeStatus,
}));

const CodeStatusFormFields: React.FC<CodeStatusFormFieldsProps> = (props) => {
    const codeStatusDescriptiveLable = useCodeStatusDescriptiveLabel(props.values.codeStatus);
    const removeDisabled = props.values.codeStatusFiles.length <= 1 && props.values.codeStatus === 'dnr';
    useEffect(() => {
        if (props.values.codeStatus === 'dnr' && props.values.codeStatusFiles.length === 0) {
            props.setFieldValue('codeStatusFiles', [getAddedFileDefaultValue()]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.values.codeStatus]);
    const noDNRFilesUploaded =
        props.values.codeStatus === 'dnr' && !props.values.codeStatusFiles.some((_) => !!_.fileId || !!_.tempFileId);

    return (
        <FormStack>
            <Field
                name="codeStatus"
                title={intl('codeStatusTitle')}
                component={FormikInputs.Select}
                selectOptions={codeStatusSelectOptions}
                isRequired
                validate={required}
            />
            {codeStatusDescriptiveLable}
            <Field name="codeStatusNotes" title={intl('codeStatusNotesTitle')} component={FormikInputs.TextArea} />
            {props.values.codeStatus === 'dnr' ? (
                <Link variant="body-1" onPress={() => Linking.openURL(advancedDirectiveFormsUrl)}>
                    {intl('findYourStatesForm')}
                </Link>
            ) : null}
            <FilesField
                values={props.values.codeStatusFiles}
                baseFieldName="codeStatusFiles"
                isDisabled={false}
                sectionTitle={intl('advancedDirectiveForms')}
                getFileSectionTitle={(index) => intl('advancedDirectiveFormTitle', { index: index + 1 })}
                removeDisabled={removeDisabled}
                addButtonAfterFileIndex={props.values.codeStatus === 'dnr' ? 1 : undefined}
            />
            {noDNRFilesUploaded ? <Text variant="body-1">{intl('advancedDirectiveFormRequiredForDNR')}</Text> : null}
        </FormStack>
    );
};

export default React.memo(CodeStatusFormFields);
